// frontend/src/services/api.js
import axios from 'axios';

// ✅ Usar variable de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Accept': 'application/json',
  },
});


// INTERCEPTOR PARA AGREGAR TOKEN
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // ✅ Si es FormData, NO tocar Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// INTERCEPTOR PARA MANEJAR ERRORES
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh/`, {
          refresh: refreshToken
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);
        
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (!window.location.pathname.includes('/login')) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// AUTH API
export const authAPI = {
  googleLogin: (token) => api.post('/auth/google/', { token }),
  getProfile: () => api.get('/auth/profile/'),
  refresh: (refreshToken) => api.post('/auth/refresh/', { refresh: refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout/', { refresh: refreshToken }),
};

// ============================================
// ✅ CLIENTES (MEJORADO)
// ============================================
export const clientsAPI = {
  // CRUD básico
  getAll: (params) => api.get('/clients/', { params }),
  getById: (id) => api.get(`/clients/${id}/`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}/`, data),
  patch: (id, data) => api.patch(`/clients/${id}/`, data),
  delete: (id) => api.delete(`/clients/${id}/`),
  
  // Búsqueda
  searchSimilar: (name) => api.get('/clients/search-similar/', { params: { name } }),
  
  // ✅ NUEVO: Contactos
  getContacts: (clientId) => api.get(`/clients/${clientId}/contacts/`),
  addContact: (clientId, data) => api.post(`/clients/${clientId}/contacts/`, data),
  updateContact: (contactId, data) => api.patch(`/contacts/${contactId}/`, data),
  deleteContact: (contactId) => api.delete(`/contacts/${contactId}/`),
  
  // ✅ NUEVO: Notas del cliente (historial con fecha y autor)
  getNotes: (clientId) => api.get(`/clients/${clientId}/notes/`),
  addNote: (clientId, data) => api.post(`/clients/${clientId}/notes/`, data),
  updateNote: (clientId, noteId, data) => api.patch(`/clients/${clientId}/notes/${noteId}/`, data),
  deleteNote: (clientId, noteId) => api.delete(`/clients/${clientId}/notes/${noteId}/`),

  // ✅ NUEVO: Cotizaciones y órdenes del cliente
  getQuotes: (clientId) => api.get(`/clients/${clientId}/quotes/`),
  getOrders: (clientId) => api.get(`/clients/${clientId}/orders/`),
  
  // ✅ NUEVO: Estadísticas
  getStats: (clientId) => api.get(`/clients/${clientId}/stats/`),
};

// COTIZACIONES
export const quotesAPI = {
  getAll: (params) => api.get('/quotes/', { params }),
  getById: (id) => api.get(`/quotes/${id}/`),
  create: (data) => api.post('/quotes/', data),
  update: (id, data) => api.put(`/quotes/${id}/`, data),
  delete: (id) => api.delete(`/quotes/${id}/`),
  uploadPDF: (file) => {
    const formData = new FormData();
    formData.append('pdf_file', file);
    return api.post('/quotes/upload-pdf/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  confirm: (data) => api.post('/quotes/confirm/', data),
  getProposalHistory: (proposalNumber) => api.get(`/quotes/proposal/${proposalNumber}/history/`),
  rejectQuote: (id) => api.post(`/quotes/${id}/reject/`),
  reactivateQuote: (id) => api.post(`/quotes/${id}/reactivate/`),
  acceptManually: (id, data) => api.post(`/quotes/${id}/accept-manually/`, data),
  getPendingFacilitation: () => api.get('/quotes/pending-facilitation/'),
};

// ÓRDENES
export const ordersAPI = {
  getAll: (params) => api.get('/orders/', { params }),
  getById: (id) => api.get(`/orders/${id}/`),
  create: (data) => api.post('/orders/', data),
  update: (id, data) => api.put(`/orders/${id}/`, data),
  patch: (id, data) => api.patch(`/orders/${id}/`, data),
  delete: (id) => api.delete(`/orders/${id}/`),
  uploadPDF: (formData) => {
    return api.post('/orders/upload-pdf/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  confirm: (data) => api.post('/orders/confirm/', data),
  matchQuotes: (amount) => api.post('/orders/match-quotes/', { amount }),
  scheduleInvoice: (orderId, data) => api.post(`/orders/${orderId}/schedule-invoice/`, data),
  updateFacilitation: (orderId, data) => api.post(`/orders/${orderId}/update-facilitation/`, data),
  getDashboardStats: (params) => api.get('/orders/dashboard-stats/', { params }),
  cleanupTemp: (data) => api.post('/orders/cleanup-temp/', data),
  createManualWithSchedule: (data) => api.post('/orders/create-manual-with-schedule/', data),
  uploadForQuote: (formData) => {
    return api.post('/orders/upload-for-quote/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  attachPDF: (orderId, file) => {
    const formData = new FormData();
    formData.append('pdf_file', file);
    return api.post(`/orders/${orderId}/attach-pdf/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

};

// ============================================
// 🆕 FACTURAS (MÓDULO COMPLETO)
// ============================================
export const invoicesAPI = {
  // CRUD básico
  getAll: (params) => api.get('/finance/invoices/', { params }),
  getById: (id) => api.get(`/finance/invoices/${id}/`),
  delete: (id) => api.delete(`/finance/invoices/${id}/`),
  
  // Upload y procesamiento
  uploadPDF: (file) => {
    const formData = new FormData();
    formData.append('pdf_file', file);
    return api.post('/finance/invoices/upload-pdf/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Confirmación de datos extraídos
  confirm: (data) => api.post('/finance/invoices/confirm/', data),
  
  // Marcar como pagada
  markPaid: (id, data) => api.patch(`/finance/invoices/${id}/mark-paid/`, data),
  
  // Cancelar factura
  cancel: (id, data) => api.post(`/finance/invoices/${id}/cancel/`, data),
};

// ============================================
// 🆕 INVOICE SCHEDULES (ACTUALIZADO)
// ============================================
export const invoiceSchedulesAPI = {
  getAll: (params) => api.get('/finance/invoice-schedules/', { params }),
  getById: (id) => api.get(`/finance/invoice-schedules/${id}/`),
  getPending: () => api.get('/finance/invoice-schedules/pending/'),
  getMyPending: () => api.get('/finance/invoice-schedules/my-pending/'),
  getNotificationsCount: () => api.get('/finance/invoice-schedules/notifications-count/'),
  create: (data) => api.post('/finance/invoice-schedules/', data),
  update: (id, data) => api.put(`/finance/invoice-schedules/${id}/`, data),
  patch: (id, data) => api.patch(`/finance/invoice-schedules/${id}/`, data),
  delete: (id) => api.delete(`/finance/invoice-schedules/${id}/`),
  uploadInvoice: (data) => {
    const formData = new FormData();
    formData.append('invoice_schedule_id', data.invoice_schedule_id);
    formData.append('pdf_file', data.pdf_file);
    formData.append('issue_date', data.issue_date);
    if (data.notes) formData.append('notes', data.notes);
    
    return api.post('/finance/invoice-schedules/upload-invoice/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// ============================================
// 🆕 CASHFLOW API
// ============================================
export const cashflowAPI = {
  // Resumen general de cashflow
  getSummary: (period = 90) => api.get('/finance/cashflow/summary/', { 
    params: { period } 
  }),
  getAnnualSummary: (year = new Date().getFullYear()) => api.get('/finance/cashflow/annual-summary/', {
    params: { year }
  }),
  
  // Proyección diaria de cashflow
  getProjection: (days = 90) => api.get('/finance/cashflow/projection/', { 
    params: { days } 
  }),
  getMonthlyProjection: (months = 12) => api.get('/finance/cashflow/monthly-projection/', {
    params: { months }
  }),
  
  // Facturas vencidas
  getOverdue: () => api.get('/finance/cashflow/overdue/'),
  
  // Cashflow agrupado por cliente
  getByClient: (params = {}) => api.get('/finance/cashflow/by-client/', { params }),
};

// ============================================
// 🆕 SALES ANALYSIS API
// ============================================
export const salesAnalysisAPI = {
  // ✅ CORRECCIÓN: Agregar /summary/ al endpoint
  getSummary: (params = {}) => api.get('/finance/sales-analysis/summary/', { params }),
  
  // Análisis por vendedor
  getBySeller: (params = {}) => api.get('/finance/sales-analysis/by-seller/', { params }),
  
  // Top clientes
  getByClient: (params = {}) => api.get('/finance/sales-analysis/by-client/', { params }),
  
  // Top servicios/productos
  getByService: (params = {}) => api.get('/finance/sales-analysis/by-service/', { params }),
  
  // Evolución temporal
  getEvolution: (params = {}) => api.get('/finance/sales-analysis/evolution/', { params }),
  
  // Comparación con período anterior
  getComparison: (params = {}) => api.get('/finance/sales-analysis/comparison/', { params }),
};


// USUARIOS (ADMIN)
export const usersAPI = {
  getPending: () => api.get('/users/pending/'),
  getAll: (params) => api.get('/users/', { params }),
  approve: (id, data) => api.post(`/users/${id}/approve/`, data),
  reject: (id) => api.post(`/users/${id}/reject/`),
  updatePermissions: (id, data) => api.patch(`/users/${id}/permissions/`, data),
  update: (id, data) => api.patch(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
};

// FACILITADORES
export const facilitatorsAPI = {
  getAll: (params) => api.get('/facilitators/', { params }),
  getById: (id) => api.get(`/facilitators/${id}/`),
  create: (data) => api.post('/facilitators/', data),
  update: (id, data) => api.put(`/facilitators/${id}/`, data),
  patch: (id, data) => api.patch(`/facilitators/${id}/`, data),
  delete: (id) => api.delete(`/facilitators/${id}/`),
};

// VENDEDORES
export const sellersAPI = {
  getAll: () => api.get('/users/sellers/'),
  getStats: (id) => api.get(`/users/${id}/stats/`),
};

export default api;
